import React, { ChangeEvent, FC, ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { isTouchDevice } from 'utils/checkIsTouchDevice';

import Icon, { IconEnum } from 'components/common/Icon';

import Tooltip from './Tooltip/Tooltip';

const TextBoxWrapper = styled.div<{ isCursorPointer?: boolean }>`
    cursor: ${(props) => props.isCursorPointer ? 'pointer' : 'default'};

`;

const Container = styled.span<{ isEditMode: boolean } >`
    position: relative;
    display: flex;
    gap: 10px;
    line-height: 20px;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    color: var(--color-primary);
    padding: 15px 10px 15px 15px;
    min-height: 50px;

    @media (max-width: 490px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 5px;
    }
`;

const Input = styled.input`
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 20px;
    width: 100%;
    flex: 1;
    background: transparent;
    border: none;
    color: var(--color-secondary);
    outline: none;
    text-align: left;
`;

const Title = styled.div`
    display: flex;
    align-items: center;
    padding: 0 10px;
    gap: 10px;
    width: 100%;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 0;

    @media (max-width: 1475px) {
        line-height: 1;
    }
`;

const Label = styled.span`
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 20px;
    background: transparent;
    border: none;
    color: var(--color-primary);

`;

const RightPartBoxWrapper = styled.div`
    display: flex;
    width: 35%;
    align-items: center;
    justify-content: space-between;
    background: var(--color-alt-second);
    color: var(--color-primary);
    padding: 15px 8px 15px 19px;
    border-radius: var(--border-radius);
    height: 50px;

    &.is-edit-mode {
         border: 1px solid var(--color-primary);
    }

    
    @media (max-width: 490px) {
        &.is-info-box {
            width: max-content;
            padding: 15px 25px;
        }
    }
`;

const ValueBox = styled.div`
    line-height: 30px;
`;

const InputIcon = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    background-color: ${(props) => props.darkMode ? 'var(--color-white)' : 'inherit'};

    img {
        color: ${(props) => props.darkMode ? 'white' : 'inherit'};
    }

`;

interface ActionsType {
    actionText: string;
    actionClick: () => void;
    isEditable: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void,
    onOutsideClick?: () => void,
}

export interface TextBoxStructureType {
    title: string;
    titleIcon?: IconEnum;
    label: string;
    value: number | string;
    labelIcon?: IconEnum;
    actions?: ActionsType;
    editedValue?: string | number;
    tooltipText?: string | React.ReactNode;
    isInfoBox?: boolean;
}

export interface TextBoxProperties {
    children?: ReactNode,
    title?: string,
    icon?: IconEnum,
    actionText?: string,
    onActionClick?: () => void,
    isEditMode?: boolean,
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void,
    label: string,
    value: string | number,
    labelIcon?: IconEnum,
    editedValue?: string | number,
    separateBoxes?: boolean,
    onOutsideClick?: () => void,
    tooltipText?: string | React.ReactNode;
    isInfoBox?: boolean;
}

const TextBox: FC<TextBoxProperties> = ({ children, title, icon, onActionClick, isEditMode, label, editedValue, value, labelIcon, onChange, onOutsideClick, tooltipText, isInfoBox }) => {
    const { user } = useUser();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isTouch, setIsTouch] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        setIsTouch(isTouchDevice());
    }, []);

    const handleTooltipToggle = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();

        if (isTouch) {
            setShowTooltip((prev) => !prev);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node) && isEditMode && onOutsideClick) {
                onOutsideClick();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditMode, onActionClick]);

    return (
        <TextBoxWrapper
            isCursorPointer={!!tooltipText}
            ref={containerRef}
            onMouseEnter={() => tooltipText && !isTouch && setShowTooltip(true)}
            onMouseLeave={() => tooltipText && !isTouch && setShowTooltip(false)}
            onClick={(e: React.MouseEvent | React.TouchEvent) => tooltipText && handleTooltipToggle(e)}>

            {title && <Title>
                {icon && <Icon icon={icon} />}
                {title}
            </Title>}

            <Container ref={containerRef} isEditMode={isEditMode}>
                <Label>{labelIcon && <Icon icon={labelIcon}/>}{label}</Label>
                <RightPartBoxWrapper className={clsx({ 'is-edit-mode': isEditMode, 'is-info-box': isInfoBox })}>
                    {isEditMode
                        ? <Input value={editedValue} alignRight={true} onChange={onChange}/>
                        : <ValueBox>{value}</ValueBox>
                    }
                    {onActionClick && <InputIcon onClick={onActionClick} darkMode={user.isDarkMode} >
                        <Icon icon={isEditMode ? IconEnum.SEND : IconEnum.EDIT}/>
                    </InputIcon>}
                    {children && children}
                </RightPartBoxWrapper>
            </Container>
            {tooltipText && <Tooltip text={tooltipText} showTooltip={showTooltip} referenceElement={containerRef.current} />}
        </TextBoxWrapper>
    );
};

export default TextBox;
